"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { useConversionEffects } from "@/hooks/ai-tool-process/use-conversion-effects";
import {
  ConvertBaseSettings,
  ConvertBaseContextState,
  useConvertBaseContextProvider,
} from "@/hooks/ai-tool-process/use-convert-base-context-provider";
import { VoiceModel } from "@/actions/db/get-models";
import { duetsSessionStorage } from "@/services/storage/duets-session-storage";
import { convertBaseInitialSettings } from "@/data/convert-base-initial-settings";
import { duetsLicenseManager } from "@/services/license/license-managers/duets-license-manager";
import { DuetConfig } from "@/components/pages/duets/hooks/use-start-duet";
import {
  DuetsSettingsField,
  DuetsSettingsStorageMixin,
} from "@/services/storage/storage-mixins/settings-mixins/duets-settings-storage-mixin";

export const MAX_DUET_VOICES_COUNT = 6;
export const MIN_DUET_VOICES_COUNT = 2;

export interface DuetsSettings extends ConvertBaseSettings {
  models: VoiceModel[];
}

export interface DuetsContextState
  extends ConvertBaseContextState<DuetsSettings> {
  handleVoiceSelect: (voice: VoiceModel) => void;
  handleDeleteVoice: (voice: VoiceModel) => void;
  handleSwitchVoice: (index: number, newVoice: VoiceModel) => void;
}

const DuetsContext = createContext<DuetsContextState | undefined>(undefined);

export const useDuetsContext = () => {
  if (!DuetsContext) {
    throw new Error("DuetContext is undefined");
  }

  return useContext(DuetsContext)!;
};

const DuetsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const handleVoiceSelect = (voice: VoiceModel) => {
    setSettings((prevState) => {
      const alreadyExists = prevState.models.some(
        (model) => model.id === voice.id,
      );

      if (alreadyExists) {
        return prevState;
      }

      return {
        ...prevState,
        models: [...prevState.models, voice],
      };
    });
  };

  const handleDeleteVoice = (voice: VoiceModel) => {
    const newModels = settings.models.filter((v) => v.id !== voice.id);
    setSettings((prevState) => ({
      ...prevState,
      models: newModels,
    }));
  };

  const handleSwitchVoice = (index: number, newVoice: VoiceModel) => {
    const getVoices = (prevVoices: VoiceModel[]) => {
      if (index < 0 || index >= prevVoices.length) return prevVoices;

      const updatedVoices = [...prevVoices];
      updatedVoices[index] = newVoice;
      return updatedVoices;
    };
    setSettings((prevState) => ({
      ...prevState,
      models: getVoices(prevState.models),
    }));
  };

  const initialSettings: DuetsSettings = {
    models: [],
    ...convertBaseInitialSettings,
  };

  const [settings, setSettings] = useState<DuetsSettings>(initialSettings);

  const baseContext = useConvertBaseContextProvider<
    DuetsSettings,
    DuetConfig,
    DuetsSettingsField,
    DuetsSettingsStorageMixin
  >(setSettings, duetsSessionStorage);
  const conversionEffects = useConversionEffects({ settings, setSettings });

  useEffect(() => {
    duetsSessionStorage.setDataFromStorage(setSettings).then();
  }, []);

  useEffect(() => {
    duetsSessionStorage.settingStorage
      .setSettings(settings.models, "models")
      .then();
  }, [settings.models]);

  return (
    <DuetsContext.Provider
      value={{
        settings,
        licenseManager: duetsLicenseManager,
        handleVoiceSelect,
        handleDeleteVoice,
        handleSwitchVoice,
        ...conversionEffects,
        ...baseContext,
      }}
    >
      {children}
    </DuetsContext.Provider>
  );
};

export default DuetsProvider;
